import React, { useState, useEffect } from 'react';
import { Card, Container, Row, Col, Table, Button, Modal, Form, Alert } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faPlus, faQuestionCircle, faUsers } from '@fortawesome/free-solid-svg-icons';
import { useSelector } from 'react-redux';
import axiosInstance from '../../api/api';
import Ranked from '../components/Ciem/Ranked';
import CardLayout from '../components/Layout/CardLayout';

const InversoresPage = () => {
    const [investors, setInvestors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showAyudaModal, setShowAyudaModal] = useState(false);
    const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
    const [selectedInvestor, setSelectedInvestor] = useState(null);
    const [formData, setFormData] = useState({
        nombre: '',
        telefono: '',
        correo: '',
        tipoInversor: ''
    });

    const ciemId = useSelector(state => state.ciem.ciemId);
    const [rankedForm, setRankedForm] = useState([]);

    // Fetch investors on component mount
    useEffect(() => {
        if (ciemId) {
            fetchInvestors();
        } else {
            setLoading(false);
        }

        axiosInstance.get('/detailoption/option/value/rankedFifteen').then((response) => {
            console.log("Calificación obtenida:", response.data.data[0]);
            setRankedForm(response.data.data[0]);
        }).catch((e) => {
            alert("Error consultando la calificacion " + e)
        });
    }, [ciemId]);

    const fetchInvestors = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get(`/potentialInvestor/${ciemId}`);
            // Handle nested data structure if needed
            const data = response.data.data || response.data;
            setInvestors(data.investors || []);
            setError(null);
        } catch (err) {
            setError('Error al cargar los inversores potenciales: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleShowCreateModal = () => {
        setModalMode('create');
        setSelectedInvestor(null);
        setFormData({
            nombre: '',
            telefono: '',
            correo: '',
            tipoInversor: ''
        });
        setShowModal(true);
    };

    const handleShowEditModal = (investor) => {
        setModalMode('edit');
        setSelectedInvestor(investor);
        setFormData({
            nombre: investor.nombre || '',
            telefono: investor.telefono || '',
            correo: investor.correo || '',
            tipoInversor: investor.tipoInversor || ''
        });
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedInvestor(null);
        setFormData({
            nombre: '',
            telefono: '',
            correo: '',
            tipoInversor: ''
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const submitData = {
                id: selectedInvestor ? selectedInvestor.id : null,
                nombre: formData.nombre,
                telefono: formData.telefono,
                correo: formData.correo,
                tipoInversor: formData.tipoInversor,
                ciemId: ciemId
            };

            // Both create and edit use POST
            await axiosInstance.post('/potentialInvestor/', submitData);
            handleCloseModal();
            fetchInvestors();
        } catch (err) {
            setError(`Error al ${modalMode === 'create' ? 'crear' : 'actualizar'} inversor potencial: ` + err.message);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('¿Está seguro de que desea eliminar este inversor potencial?')) {
            try {
                await axiosInstance.delete(`/potentialInvestor/${id}`);
                fetchInvestors();
            } catch (err) {
                setError('Error al eliminar inversor potencial: ' + err.message);
            }
        }
    };

    if (!ciemId) {
        return (
            <Container className="mt-4">
                <Alert variant="warning">
                    Por favor, seleccione un CIEM para ver los inversores potenciales.
                </Alert>
            </Container>
        );
    }

    return (
        <Container fluid className="px-0">
            <Row>
                <Col xs={12} className="p-3">
                    <Card>
                        <Card.Header>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <div className="d-flex align-items-center">
                                    <h3 className="mb-0 me-3">
                                        <FontAwesomeIcon icon={faUsers} className="me-2" />
                                        Inversores
                                    </h3>
                                    <Button
                                        variant="outline-info"
                                        size="sm"
                                        className="me-2"
                                        onClick={() => setShowAyudaModal(true)}
                                        title="Ayuda"
                                    >
                                        <FontAwesomeIcon icon={faQuestionCircle} className="me-1" />
                                        Ayuda
                                    </Button>
                                </div>
                                <Button variant="primary" onClick={handleShowCreateModal}>
                                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                                    Crear
                                </Button>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Cargando...</span>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <Table responsive hover bordered>
                                        <thead>
                                            <tr>
                                                <th>Nombre</th>
                                                <th>Teléfono de contacto</th>
                                                <th>Correo electrónico</th>
                                                <th>Tipo de inversor*</th>
                                                <th>Acciones</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {investors.length === 0 ? (
                                                <tr>
                                                    <td colSpan="5" className="text-center py-4">
                                                        No hay inversores potenciales registrados
                                                    </td>
                                                </tr>
                                            ) : (
                                                investors.map((investor) => (
                                                    <tr key={investor.id}>
                                                        <td>{investor.nombre || '-'}</td>
                                                        <td>{investor.telefono || '-'}</td>
                                                        <td>{investor.correo || '-'}</td>
                                                        <td>{investor.tipoInversor || '-'}</td>
                                                        <td>
                                                            <Button
                                                                variant="info"
                                                                size="sm"
                                                                className="me-2"
                                                                onClick={() => handleShowEditModal(investor)}
                                                            >
                                                                <FontAwesomeIcon icon={faEdit} />
                                                            </Button>
                                                            <Button
                                                                variant="danger"
                                                                size="sm"
                                                                onClick={() => handleDelete(investor.id)}
                                                            >
                                                                <FontAwesomeIcon icon={faTrash} />
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </Table>
                                    <p className="mt-3 text-muted" style={{ fontSize: '0.9em' }}>
                                        <em>*Tipo de inversor:</em> personal natural, programa de emprendimiento y empresa privada.
                                    </p>
                                </>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Create/Edit Modal */}
            <Modal show={showModal} onHide={handleCloseModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {modalMode === 'create' ? 'Crear Nuevo Inversor Potencial' : 'Editar Inversor Potencial'}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Nombre</Form.Label>
                            <Form.Control
                                type="text"
                                name="nombre"
                                value={formData.nombre}
                                onChange={handleInputChange}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Teléfono de contacto</Form.Label>
                            <Form.Control
                                type="text"
                                name="telefono"
                                value={formData.telefono}
                                onChange={handleInputChange}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Correo electrónico</Form.Label>
                            <Form.Control
                                type="email"
                                name="correo"
                                value={formData.correo}
                                onChange={handleInputChange}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Tipo de inversor</Form.Label>
                            <Form.Select
                                name="tipoInversor"
                                value={formData.tipoInversor}
                                onChange={handleInputChange}
                            >
                                <option value="">Seleccione un tipo</option>
                                <option value="personal natural">Personal natural</option>
                                <option value="programa de emprendimiento">Programa de emprendimiento</option>
                                <option value="empresa privada">Empresa privada</option>
                            </Form.Select>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal}>
                            Cancelar
                        </Button>
                        <Button variant="primary" type="submit">
                            {modalMode === 'create' ? 'Crear' : 'Actualizar'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Modal de Ayuda */}
            <Modal show={showAyudaModal} onHide={() => setShowAyudaModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Ayuda</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div style={{ textAlign: 'justify' }}>
                        <p>
                            Defina las personas, empresas, instituciones o programas de emprendimiento que podrían interesarse en invertir en su propuesta emprendedora.
                        </p>
                        <p>
                            Para definir los posibles inversores (personas naturales, empresas, instituciones o programas de apoyo al emprendimiento) se sugiere lo siguiente:
                        </p>
                        <p>
                            Haga una relación de familiares, amigos y relacionados que podrían interesarse en invertir en su idea de negocio que vivan dentro o fuera del país. Incluya personas que no sean propiamente inversores o adineradas, basta con que se considere que la persona cuente con un empleo o actividad empresarial que le podría permitir invertir empleando la estrategia del ahorro-aporte. En esta una persona ahorra una parte de su ingreso para invertir, además podría destinar parte de su ingreso a pagar un crédito que tomase con una entidad financiera para poder invertir en la propuesta emprendedora.
                        </p>
                        <p>
                            Liste las empresas a las que podría interesarles invertir en la idea de negocio, pues los productos considerados complementan de alguna manera a los suyos o, incluso, tal interés podría surgir de vincularse a un naciente competidor directo. Así mismo, incluya en la relación a entidades e inversionistas reconocidos como inversores de capital de riesgo que eventualmente podrían interesarse en aplicar recursos en el proyecto.
                        </p>
                        <p>
                            Relacione las instituciones estatales, privadas, internacionales y entidades multilaterales a las que se podría contactar para participar con la idea de negocio en sus programas de apoyo al emprendimiento, pues en ellos se puede obtener desde ayuda para hacer el plan de negocio hasta recursos de diverso orden, incluidos préstamos de carácter condonable.
                        </p>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowAyudaModal(false)}>
                        Cerrar
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Ranked - Calificación */}
            {(() => {
                const calificacion = <Ranked field={rankedForm} ciemId={ciemId} />;
                return <CardLayout value={"rankedFifteen"} tablaComponent={calificacion} />;
            })()}
        </Container>
    );
};

export default InversoresPage;
